import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const BreakdownRecovery = () => {
  // State variables for Breakdown Recovery calculation
  const [distanceToRecoverySite, setDistanceToRecoverySite] = useState(0); // Distance to the recovery site in kilometers or miles
  const [averageRecoverySpeed, setAverageRecoverySpeed] = useState(0); // Average recovery speed in km/h or mph
  const [breakdownRecoveryTime, setBreakdownRecoveryTime] = useState(0); // Calculated breakdown recovery time in hours

  // Event handlers to update state values
  const handleDistanceChange = (e) =>
    setDistanceToRecoverySite(parseFloat(e.target.value) || 0);
  const handleSpeedChange = (e) =>
    setAverageRecoverySpeed(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate the Breakdown Recovery Time
  useEffect(() => {
    if (averageRecoverySpeed > 0 && distanceToRecoverySite > 0) {
      // Breakdown Recovery Time = Distance to Recovery Site / Average Recovery Speed
      const recoveryTime = distanceToRecoverySite / averageRecoverySpeed;
      setBreakdownRecoveryTime(recoveryTime);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [distanceToRecoverySite, averageRecoverySpeed]);

  return (
    <Layout
      title="Breakdown Recovery Calculator (Logistics)"
      path="breakdown-recovery"
    >
      <section className="section-padding" id="breakdown-recovery">
        <div className="container mt-5">
          <div className="row">
            {/* Breakdown Recovery Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Breakdown Recovery Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Distance to Recovery Site */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="distanceToRecoverySite"
                        className="form-label"
                      >
                        Distance to Recovery Site
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Distance to Recovery Site:</strong> The total distance from the breakdown location to the recovery site, in kilometers or miles."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="distanceToRecoverySite"
                        placeholder="Enter distance to recovery site"
                        value={distanceToRecoverySite || ""}
                        onChange={handleDistanceChange}
                      />
                    </div>

                    {/* Input for Average Recovery Speed */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="averageRecoverySpeed"
                        className="form-label"
                      >
                        Average Recovery Speed
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Average Recovery Speed:</strong> The speed at which the recovery vehicle or process progresses, in kilometers per hour or miles per hour."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="averageRecoverySpeed"
                        placeholder="Enter average recovery speed"
                        value={averageRecoverySpeed || ""}
                        onChange={handleSpeedChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Breakdown Recovery Time:{" "}
                        <span className="fw-bold ms-3">
                          {breakdownRecoveryTime === 0
                            ? "0"
                            : breakdownRecoveryTime.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          {" hours"}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Breakdown Recovery
                </h3>

                {/* What is Breakdown Recovery? */}
                <h4 className="mt-3 mb-3">What is Breakdown Recovery?</h4>
                <p>
                  The **Breakdown Recovery** refers to the time required to
                  recover a vehicle that has broken down, measured from the
                  point the vehicle breaks down to the point it reaches the
                  recovery site. This tool estimates the recovery time based on
                  the distance to the recovery site and the average recovery
                  speed.
                </p>

                {/* Why Use the Breakdown Recovery Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use the Breakdown Recovery Calculator?
                </h4>
                <p>
                  By using this calculator, fleet managers and logistics
                  companies can estimate the time it will take to recover a
                  vehicle from a breakdown. This helps in planning response
                  times, optimizing resources, and minimizing disruption to
                  operations.
                </p>

                {/* Benefits of Using the Breakdown Recovery Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Breakdown Recovery Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Helps in Planning Response Times and Resource Allocation:
                    </strong>{" "}
                    Enables businesses to allocate resources effectively and
                    plan for expected recovery times.
                  </li>
                  <li>
                    <strong>Supports Fleet Management Efficiency:</strong> Helps
                    fleet managers optimize operations and reduce downtime.
                  </li>
                  <li>
                    <strong>Useful for Cost Estimation in Logistics:</strong>{" "}
                    Provides valuable data to estimate recovery-related costs
                    and improve cost management.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Breakdown Recovery Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Logistics Companies:</strong> Assists in estimating
                    breakdown recovery time for fleet operations.
                  </li>
                  <li>
                    <strong>Fleet Managers:</strong> Helps fleet managers plan
                    and allocate resources for vehicle recovery.
                  </li>
                  <li>
                    <strong>Transportation Companies:</strong> Useful for
                    transportation companies to improve efficiency and response
                    times in the event of a breakdown.
                  </li>
                </ul>

                {/* Two Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How can Breakdown Recovery Time impact fleet operations?
                </h4>
                <p>
                  Breakdown Recovery Time can significantly impact fleet
                  operations by causing delays and inefficiencies. Accurate
                  estimates help minimize these impacts and ensure timely
                  repairs and service.
                </p>

                <h4 className="mt-4 mb-3">
                  How do I improve my fleet's recovery time?
                </h4>
                <p>
                  Improving recovery time involves optimizing response
                  processes, training staff for faster deployment, and
                  maintaining a well-equipped recovery team to respond to
                  breakdowns quickly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BreakdownRecovery;
